import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminKycQueueGrouped, adminKycDocUrl, adminKycApprove, adminKycReject,
} from "@lawyer/services/kycApi";
import { KycLawyerGroup } from "@admin/components/kyc/KycLawyerGroup";
import { DigiLockerDocDialog } from "@admin/components/kyc/DigiLockerDocDialog";
import { notifyApprovalsChanged } from "@admin/services/adminApi";

const TABS = [
  { key: "pending_review", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];
const PAGE = 20;

export default function AdminKycReview() {
  const [status, setStatus] = useState("pending_review");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, total_documents: 0, items: [] });
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput.trim()); setSkip(0); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminKycQueueGrouped({ status, q: q || undefined, skip, limit: PAGE });
      setData(r);
      notifyApprovalsChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
    finally { setLoading(false); }
  }, [status, q, skip]);
  useEffect(() => { reload(); }, [reload]);

  const changeTab = (key) => { setStatus(key); setSkip(0); };

  const [dlItem, setDlItem] = useState(null);

  const openUrl = async (item) => {
    setBusy(`view-${item.id}`);
    try {
      const r = await adminKycDocUrl(item.lawyer_id, item.id);
      const url = r.url?.startsWith("http") ? r.url : `${process.env.REACT_APP_BACKEND_URL}${r.url}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch URL"); }
    finally { setBusy(null); }
  };
  const view = async (item) => {
    if (item.source === "digilocker") { setDlItem(item); return; }
    await openUrl(item);
  };
  const approve = async (item) => {
    const unreadablePan = item.source === "digilocker" && item.doc_type === "pan" && item.pan_number_readable === false;
    if (unreadablePan && !window.confirm("PAN number could not be read from this DigiLocker file. Approving will NOT set the lawyer's PAN — requirement #7 stays unmet. Approve anyway?")) return;
    setBusy(`ok-${item.id}`);
    try {
      const res = await adminKycApprove(item.lawyer_id, item.id);
      if (res?.warning) toast.warning(res.warning, { duration: 8000 }); else toast.success("Approved");
      reload();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const reject = async (item) => {
    const reason = window.prompt("Reason for rejection (shown to the lawyer)?");
    if (!reason) return;
    setBusy(`no-${item.id}`);
    try { await adminKycReject(item.lawyer_id, item.id, reason); toast.success("Rejected"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const override = async (item, reason) => {
    setBusy(`override-${item.id}`);
    try {
      if (item.status === "rejected") {
        await adminKycApprove(item.lawyer_id, item.id, "Rejection reversed by admin");
        toast.success("Rejection reversed — document approved");
      } else {
        await adminKycReject(item.lawyer_id, item.id, reason);
        toast.success("Approval revoked — document rejected");
      }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const pageEnd = Math.min(skip + PAGE, data.total);

  return (
    <div className="space-y-5" data-testid="admin-kyc-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#D4AF37]" />
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>e-KYC Review</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">Approve or reject lawyer onboarding documents, grouped by lawyer. Approving all required docs auto-verifies the lawyer.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="kyc-search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search lawyer name / email / phone..."
            className="h-9 rounded-xl pl-8"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="kyc-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => changeTab(t.key)}
            data-testid={`kyc-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
              status === t.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden ${loading ? "opacity-70" : ""}`} data-testid="kyc-list">
        {data.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="kyc-empty">
            {q ? `No lawyers matching “${q}” in this bucket.` : "No documents in this bucket."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {data.items.map((g) => (
              <KycLawyerGroup key={g.lawyer_id} group={g} busy={busy} onView={view} onApprove={approve} onReject={reject} onOverride={override} />
            ))}
          </ul>
        )}
      </div>

      <DigiLockerDocDialog item={dlItem} onClose={() => setDlItem(null)} onDownload={openUrl} busy={!!busy} />

      <div className="flex flex-wrap items-center gap-3" data-testid="kyc-pagination">
        <p className="text-[11px] text-slate-400" data-testid="kyc-totals">
          {data.total} lawyer{data.total === 1 ? "" : "s"} · {data.total_documents} document{data.total_documents === 1 ? "" : "s"} in this bucket
          {data.total > 0 ? ` · showing ${skip + 1}–${pageEnd}` : ""}
        </p>
        <div className="ml-auto flex gap-1">
          <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs" data-testid="kyc-prev"
            disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - PAGE))}>
            <ChevronLeft size={12} /> Prev
          </Button>
          <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs" data-testid="kyc-next"
            disabled={pageEnd >= data.total || loading} onClick={() => setSkip(skip + PAGE)}>
            Next <ChevronRight size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}
